import { useRouter } from "@/_router";
import { useUserSlice } from "@/models/useUserSlice";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Settings from "./settings";
import {
  SettingsIcon,
  CrownIcon,
  LibraryIcon,
  CloseIcon,
  HeartIcon,
  HeartFilledIcon,
  ShareIcon,
} from "@/components/Icons";
import { useUISlice } from "@/models/useUISlice";
import Skeleton from "@/components/Skeleton";
import NativeContainer from "@/components/NativeContainer";
import { useAuth } from "@/models/useAuth";
import { getPlatform, isNativeApp } from "@/models/platform";
import { LocalNotifications } from "@capacitor/local-notifications";
import { App } from "@capacitor/app";
import { version } from "../../../package.json";
import { updateStatusBarStyle } from "@/models/useTheme";
import { logFunnelEvent } from "@/fetchData/datadog";
import { requestReview, shareBook } from "@/models/useNative";
import type { Book, ContentLanguage } from "@/models/types";
import { CONTENT_LANGUAGES } from "@/models/types";
import { i18n } from "@/locales";
import { useDismissSheet } from "@/models/useDismissSheet";
import { useAndroidBackButton } from "@/models/useAndroidBackButton";

const DAILY_NOTIFICATION_ID = 1001;

/** Schedule a daily engagement notification at 9 AM with personalized book content */
const scheduleDailyNotification = async (
  homeBooks: Book[],
  readingProgress: {
    [bookId: string]: { lastChapter: number; lastReadAt: string };
  }
) => {
  if (!isNativeApp()) return;

  // Pick the best book to recommend
  let selectedBook: Book | undefined;
  let chapter = 1;
  let isResuming = false;

  // Priority 1: most recently read book
  let bestTime = 0;
  for (const book of homeBooks) {
    const p = readingProgress[book.bookId];
    if (p) {
      const t = new Date(p.lastReadAt).getTime();
      if (t > bestTime) {
        bestTime = t;
        selectedBook = book;
        chapter = p.lastChapter;
        isResuming = true;
      }
    }
  }

  // Priority 2: random book from homeBooks
  if (!selectedBook && homeBooks.length > 0) {
    selectedBook = homeBooks[Math.floor(Math.random() * homeBooks.length)];
  }

  if (!selectedBook) return;

  const title = i18n.t("home.dailyNotificationTitle");
  const body = isResuming
    ? i18n.t("home.dailyNotificationContinue", {
        title: selectedBook.aiTitle,
        chapter,
      })
    : i18n.t("home.dailyNotificationDiscover");

  // Next 9 AM
  const now = new Date();
  const next = new Date(now);
  next.setHours(9, 0, 0, 0);
  if (next <= now) next.setDate(next.getDate() + 1);

  // Cancel existing before rescheduling (idempotent)
  await LocalNotifications.cancel({
    notifications: [{ id: DAILY_NOTIFICATION_ID }],
  });

  await LocalNotifications.schedule({
    notifications: [
      {
        id: DAILY_NOTIFICATION_ID,
        title,
        body,
        schedule: { at: next, every: "day", allowWhileIdle: true },
        extra: { bookId: selectedBook.bookId, chapter: String(chapter) },
      },
    ],
  });
};

/** Pre-permission prompt shown before requesting native notification permissions */
const NotificationPrompt = ({
  onAllow,
  onDeny,
}: {
  onAllow: () => void;
  onDeny: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <div className="fixed inset-0 z-50" onClick={onDeny}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-md" />
      <div className="absolute inset-0 flex items-center justify-center px-8">
        <div
          className="w-full max-w-sm bg-[var(--glass-bg)] backdrop-blur-2xl rounded-[32px] p-8 shadow-2xl border border-white/10 text-center"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="text-5xl mb-4">🔔</div>
          <h2 className="text-[var(--text-primary)] text-xl font-bold mb-3">
            {t("home.notificationPromptTitle")}
          </h2>
          <p className="text-[var(--text-secondary)] text-base leading-relaxed mb-8">
            {t("home.notificationPromptDescription")}
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={onAllow}
              className="w-full h-14 rounded-full bg-white/20 backdrop-blur-md text-[var(--text-primary)] font-bold text-base active:scale-[0.97] transition-transform border border-white/20"
            >
              {t("home.notificationPromptAllow")}
            </button>
            <button
              onClick={onDeny}
              className="w-full h-14 rounded-full bg-white/10 text-[var(--text-secondary)] font-bold text-base active:scale-[0.97] transition-transform"
            >
              {t("home.notificationPromptDeny")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Scroll tutorial overlay for first-time users */
const ScrollTutorial = ({ onDismiss }: { onDismiss: () => void }) => {
  const { t } = useTranslation();

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative flex flex-col items-center gap-4 animate-fade-in-up">
        <p className="text-white text-lg font-bold px-6 text-center">
          {t("home.scrollTutorial")}
        </p>
        <div className="animate-scroll-hint">
          <svg
            width="36"
            height="36"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
        <button
          onClick={onDismiss}
          className="mt-4 px-8 py-3 rounded-full bg-white/20 backdrop-blur-md text-white font-bold text-base active:scale-95 transition-transform border border-white/20"
        >
          {t("home.gotIt")}
        </button>
      </div>

      <style>{`
        @keyframes fade-in-up {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes scroll-hint {
          0%, 100% { opacity: 0.4; transform: translateY(0); }
          50% { opacity: 1; transform: translateY(12px); }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out both;
        }
        .animate-scroll-hint {
          animation: scroll-hint 1.2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

/** Book detail bottom sheet — full description + read now */
const BookDetailSheet = ({
  book,
  onClose,
  onRead,
}: {
  book: Book;
  onClose: () => void;
  onRead: () => void;
}) => {
  const { t } = useTranslation();
  const likedBooks = useUserSlice((state) => state.likedBooks);
  const isLiked = likedBooks.includes(book.bookId);

  const toggleLiked = () => {
    const pref = useUserSlice.getState().preference();
    if (isLiked) {
      pref.removeFromLikedBooks(book.bookId);
    } else {
      pref.addToLikedBooks(book.bookId);
    }
  };

  return (
    <div className="fixed inset-0 z-50" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-md" />
      <div
        className="absolute bottom-0 left-0 right-0 max-h-[80dvh] bg-white/10 backdrop-blur-2xl rounded-t-[40px] overflow-y-auto border-t border-white/20"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pb-[calc(env(safe-area-inset-bottom,24px)+24px)] pt-6">
          {book.aiDescription && (
            <p className="text-white/90 text-base leading-relaxed mb-4 font-medium">
              {book.aiDescription}
            </p>
          )}

          <div className="flex gap-3">
            <button
              onClick={() => {
                onClose();
                onRead();
              }}
              className="flex-1 h-16 rounded-[32px] bg-white/20 backdrop-blur-md text-white font-bold text-lg active:scale-[0.97] transition-transform border border-white/20 flex items-center justify-center"
            >
              {t("home.readNow")}
            </button>
            <button
              onClick={toggleLiked}
              className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-md border border-white/20 flex items-center justify-center active:scale-90 transition-transform"
            >
              {isLiked ? (
                <HeartFilledIcon size={26} className="text-red-500" />
              ) : (
                <HeartIcon size={26} className="text-white" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Language picker bottom sheet */
const LanguagePicker = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation();
  const currentLang = useUserSlice((state) => state.contentLanguage);

  const handleSelect = (code: ContentLanguage) => {
    useUserSlice.getState().setContentLanguage(code);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50" onClick={onClose}>
      <div className="absolute inset-0 bg-[var(--bg-primary)]/50 backdrop-blur-sm" />
      <div
        className="absolute bottom-0 left-0 right-0 max-h-[70dvh] bg-[var(--glass-bg)] backdrop-blur-3xl rounded-t-[40px] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[var(--glass-bg)]/80 backdrop-blur-md pt-6 pb-2 px-6 z-10">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-[var(--text-primary)] font-bold text-2xl tracking-tight">
              {t("home.contentLanguageTitle")}
            </h2>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-[var(--glass-bg-hover)] active:bg-[var(--glass-bg-active)] transition-colors"
            >
              <CloseIcon size={22} className="text-[var(--text-secondary)]" />
            </button>
          </div>
        </div>
        <div className="px-6 pb-[calc(env(safe-area-inset-bottom,24px)+24px)] pt-2">
          <div className="flex flex-col gap-1">
            {CONTENT_LANGUAGES.map(({ code, flag }) => (
              <button
                key={code}
                onClick={() => handleSelect(code)}
                className="flex items-center justify-between px-4 py-4 rounded-2xl active:bg-white/10 transition-colors"
              >
                <span className="text-[var(--text-primary)] text-base font-medium">
                  {flag} {t(`home.contentLanguage.${code}`)}
                </span>
                {currentLang === code && (
                  <span className="text-[var(--accent)] text-lg font-bold">
                    ✓
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/** Individual feed card with blur-up image loading */
const FeedCard = ({
  book,
  onRead,
  onReadBook,
  onSettingsOpenChange,
}: {
  book: Book;
  onRead: () => void;
  onReadBook: (bookId: string) => void;
  onSettingsOpenChange?: (open: boolean) => void;
}) => {
  const { t } = useTranslation();
  const [imgLoaded, setImgLoaded] = useState(false);
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const isSubscribed = useUserSlice((state) => state.isSubscribed);
  const contentLanguage = useUserSlice((state) => state.contentLanguage);
  const contentFlag = useMemo(
    () =>
      CONTENT_LANGUAGES.find((l) => l.code === contentLanguage)?.flag ??
      contentLanguage,
    [contentLanguage]
  );

  useDismissSheet(
    isDetailOpen,
    useCallback(() => setIsDetailOpen(false), [])
  );
  useDismissSheet(
    isLibraryOpen,
    useCallback(() => setIsLibraryOpen(false), [])
  );
  useDismissSheet(
    isLangOpen,
    useCallback(() => setIsLangOpen(false), [])
  );

  return (
    <div className="h-[100dvh] snap-start snap-always relative">
      {/* Full-screen cover image with blur-up */}
      <button onClick={onRead} className="absolute inset-0 w-full h-full">
        {book.aiCoverBlurDataUri && (
          <div
            className="absolute inset-0 w-full h-full"
            style={{
              backgroundImage: `url(${book.aiCoverBlurDataUri})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        )}
        {book.aiCoverUrl ? (
          <img
            src={book.aiCoverUrl}
            alt=""
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${imgLoaded ? "opacity-100" : "opacity-0"}`}
            loading="lazy"
            decoding="async"
            onLoad={() => setImgLoaded(true)}
          />
        ) : (
          <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-neutral-800 to-neutral-950" />
        )}
      </button>

      {/* Right-side action column */}
      <div className="absolute right-4 bottom-[25%] flex flex-col items-center gap-6 z-10">
        {!isSubscribed && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              useUISlice
                .getState()
                .setPaywallVisible({ utmSource: "home_crown_button" });
            }}
            className="flex flex-col items-center gap-1 active:scale-90 transition-transform animate-bounce"
          >
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 shadow-[0_0_16px_rgba(251,191,36,0.5)] flex items-center justify-center">
              <CrownIcon size={28} className="text-white drop-shadow-md" />
            </div>
          </button>
        )}

        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsLangOpen(true);
          }}
          className="flex flex-col items-center gap-1 active:scale-90 transition-transform"
        >
          <div className="w-14 h-14 rounded-full bg-[var(--glass-bg)] backdrop-blur-2xl shadow-lg flex items-center justify-center">
            <span className="text-2xl drop-shadow-md">{contentFlag}</span>
          </div>
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsLibraryOpen(true);
          }}
          className="flex flex-col items-center gap-1 active:scale-90 transition-transform"
        >
          <div className="w-14 h-14 rounded-full bg-[var(--glass-bg)] backdrop-blur-2xl shadow-lg flex items-center justify-center">
            <LibraryIcon
              size={28}
              className="text-[var(--text-primary)] drop-shadow-md"
            />
          </div>
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            shareBook(book);
          }}
          className="flex flex-col items-center gap-1 active:scale-90 transition-transform"
        >
          <div className="w-14 h-14 rounded-full bg-[var(--glass-bg)] backdrop-blur-2xl shadow-lg flex items-center justify-center">
            <ShareIcon
              size={28}
              className="text-[var(--text-primary)] drop-shadow-md"
            />
          </div>
        </button>

        <SettingsButton onOpenChange={onSettingsOpenChange} />
      </div>

      {/* Bottom gradient overlay with tappable description */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent pt-24 pb-[calc(env(safe-area-inset-bottom,24px)+24px)] px-6 pointer-events-none">
        <div className="max-w-[480px] mx-auto pointer-events-auto pr-20">
          {book.aiDescription && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsDetailOpen(true);
              }}
              className="text-left active:opacity-70 transition-opacity"
            >
              <p className="text-white/95 text-base font-medium leading-relaxed line-clamp-3 drop-shadow-md">
                {book.aiDescription}
              </p>
              <p className="text-white/70 text-sm font-bold mt-2 drop-shadow-md">
                {t("home.moreInfo")}
              </p>
            </button>
          )}
        </div>
      </div>

      {isDetailOpen && (
        <BookDetailSheet
          book={book}
          onClose={() => setIsDetailOpen(false)}
          onRead={onRead}
        />
      )}

      {isLibraryOpen && (
        <LibrarySheet
          onClose={() => setIsLibraryOpen(false)}
          onRead={onReadBook}
        />
      )}

      {isLangOpen && <LanguagePicker onClose={() => setIsLangOpen(false)} />}
    </div>
  );
};

/** Settings button that opens settings sheet */
const SettingsButton = ({
  onOpenChange,
}: {
  onOpenChange?: (open: boolean) => void;
}) => {
  const [open, setOpen] = useState(false);

  const updateOpen = useCallback(
    (value: boolean) => {
      setOpen(value);
      onOpenChange?.(value);
    },
    [onOpenChange]
  );

  useDismissSheet(
    open,
    useCallback(() => updateOpen(false), [updateOpen])
  );

  return (
    <>
      <button
        onClick={(e) => {
          e.stopPropagation();
          updateOpen(true);
        }}
        className="flex flex-col items-center gap-1 active:scale-90 transition-transform"
      >
        <div className="w-14 h-14 rounded-full bg-[var(--glass-bg)] backdrop-blur-2xl shadow-lg flex items-center justify-center">
          <SettingsIcon
            size={28}
            className="text-[var(--text-primary)] drop-shadow-md"
          />
        </div>
      </button>
      {open && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-[var(--bg-primary)]/40 backdrop-blur-sm"
            onClick={() => updateOpen(false)}
          />
          <div className="absolute top-0 right-0 h-full w-full max-w-md bg-[var(--bg-primary)] shadow-2xl animate-slide-in-right">
            <Settings onClose={() => updateOpen(false)} />
          </div>
        </div>
      )}
    </>
  );
};

/** Library sheet overlay */
const LibrarySheet = ({
  onClose,
  onRead,
}: {
  onClose: () => void;
  onRead: (bookId: string) => void;
}) => {
  const { t } = useTranslation();
  const likedBooks = useUserSlice((state) => state.likedBooks);
  const readingProgress = useUserSlice((state) => state.readingProgress);
  const homeBooks = useUserSlice((state) => state.homeBooks);
  const [tab, setTab] = useState<"reading" | "liked">("reading");

  const bookMap = useMemo(() => {
    const map = new Map<string, Book>();
    homeBooks.forEach((b) => map.set(b.bookId, b));
    return map;
  }, [homeBooks]);

  const inProgressBooks = useMemo(() => {
    return Object.entries(readingProgress)
      .sort(
        ([, a], [, b]) =>
          new Date(b.lastReadAt).getTime() - new Date(a.lastReadAt).getTime()
      )
      .map(([bookId, prog]) => ({ book: bookMap.get(bookId), progress: prog }))
      .filter((x) => x.book);
  }, [readingProgress, bookMap]);

  const likedBooksList = useMemo(() => {
    return likedBooks.map((id) => bookMap.get(id)).filter(Boolean) as Book[];
  }, [likedBooks, bookMap]);

  const handleBookRead = (bookId: string) => {
    onClose();
    onRead(bookId);
  };

  return (
    <div className="fixed inset-0 z-50" onClick={onClose}>
      <div className="absolute inset-0 bg-[var(--bg-primary)]/50 backdrop-blur-sm" />
      <div
        className="absolute bottom-0 left-0 right-0 max-h-[85dvh] bg-[var(--glass-bg)] backdrop-blur-3xl rounded-t-[40px] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[var(--glass-bg)]/80 backdrop-blur-md pt-6 pb-2 px-6 z-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[var(--text-primary)] font-bold text-2xl tracking-tight">
              {t("home.library")}
            </h2>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-[var(--glass-bg-hover)] active:bg-[var(--glass-bg-active)] transition-colors"
            >
              <CloseIcon size={22} className="text-[var(--text-secondary)]" />
            </button>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setTab("reading")}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                tab === "reading"
                  ? "bg-[var(--accent)]/15 text-[var(--accent)]"
                  : "text-[var(--text-secondary)] bg-black/5 dark:bg-white/5"
              }`}
            >
              {t("home.readingTab")}
            </button>
            <button
              onClick={() => setTab("liked")}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors flex items-center gap-1.5 ${
                tab === "liked"
                  ? "bg-[var(--accent)]/15 text-[var(--accent)]"
                  : "text-[var(--text-secondary)] bg-black/5 dark:bg-white/5"
              }`}
            >
              <HeartFilledIcon size={14} />
              {t("home.likedTab")}
            </button>
          </div>
        </div>

        <div className="px-6 pb-[calc(env(safe-area-inset-bottom,24px)+24px)] pt-4">
          {tab === "reading" && (
            <>
              {inProgressBooks.length > 0 ? (
                <div className="flex flex-col gap-3">
                  {inProgressBooks.map(({ book, progress }) => (
                    <button
                      key={book!.bookId}
                      onClick={() => handleBookRead(book!.bookId)}
                      className="flex items-center gap-4 p-3 rounded-[24px] bg-[var(--glass-bg-hover)] backdrop-blur-md active:scale-[0.98] transition-all text-left shadow-lg"
                    >
                      {book!.aiCoverUrl ? (
                        <img
                          src={book!.aiCoverUrl}
                          alt=""
                          className="w-16 h-20 rounded-[16px] object-cover flex-shrink-0 shadow-md"
                        />
                      ) : (
                        <div className="w-16 h-20 rounded-[16px] bg-[var(--glass-bg)] flex-shrink-0 shadow-md" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-[var(--text-primary)] text-lg font-bold truncate">
                          {book!.aiTitle}
                        </p>
                        <p className="text-[var(--text-secondary)] text-sm font-medium mt-1">
                          {t("home.chapterProgress", {
                            current: progress.lastChapter,
                            total: book!.chapterCount,
                          })}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-[var(--text-muted)] text-sm text-center py-8">
                  {t("home.emptyReadingState")}
                </p>
              )}
            </>
          )}

          {tab === "liked" && (
            <>
              {likedBooksList.length > 0 ? (
                <div className="grid grid-cols-3 gap-4">
                  {likedBooksList.map((book) => (
                    <button
                      key={book.bookId}
                      onClick={() => handleBookRead(book.bookId)}
                      className="active:scale-95 transition-transform"
                    >
                      {book.aiCoverUrl ? (
                        <img
                          src={book.aiCoverUrl}
                          alt=""
                          className="w-full aspect-[9/19.5] rounded-[20px] object-cover shadow-lg"
                        />
                      ) : (
                        <div className="w-full aspect-[9/19.5] rounded-[20px] bg-[var(--glass-bg)] shadow-lg" />
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-[var(--text-muted)] text-sm text-center py-8">
                  {t("home.emptyLikedState")}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Home = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const homeBooks = useUserSlice((state) => state.homeBooks);
  const readingProgress = useUserSlice((state) => state.readingProgress);

  useEffect(() => {
    updateStatusBarStyle("dark");
  }, []);

  // Navigate to reader when reading notification is tapped
  useEffect(() => {
    if (!isNativeApp()) return;

    const listener = LocalNotifications.addListener(
      "localNotificationActionPerformed",
      (event) => {
        const { bookId, chapter } = event.notification.extra || {};
        if (bookId && chapter) {
          router.replace(`/native/reader?bookId=${bookId}&chapter=${chapter}`);
        }
      }
    );

    return () => {
      listener.then((l) => l.remove());
    };
  }, [router]);

  // Show notification prompt once, then schedule daily notifications on subsequent opens
  const hasSeenNotificationPrompt = useUserSlice(
    (state) => state.hasSeenNotificationPrompt
  );
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);

  useEffect(() => {
    if (homeBooks.length === 0 || !isNativeApp()) return;

    if (hasSeenNotificationPrompt) {
      // Already accepted before — reschedule to keep content fresh
      scheduleDailyNotification(homeBooks, readingProgress);
    } else {
      setShowNotificationPrompt(true);
    }
  }, [homeBooks, readingProgress, hasSeenNotificationPrompt]);

  const handleNotificationAllow = useCallback(async () => {
    setShowNotificationPrompt(false);
    useUserSlice.getState().preference().setHasSeenNotificationPrompt();
    logFunnelEvent("notification:prompted");

    const perm = await LocalNotifications.requestPermissions();
    if (perm.display === "granted") {
      logFunnelEvent("notification:accepted");
      scheduleDailyNotification(
        useUserSlice.getState().homeBooks,
        useUserSlice.getState().readingProgress
      );
    } else {
      logFunnelEvent("notification:denied");
    }
  }, []);

  const handleNotificationDeny = useCallback(() => {
    setShowNotificationPrompt(false);
    useUserSlice.getState().preference().setHasSeenNotificationPrompt();
    logFunnelEvent("notification:denied");
  }, []);

  const backFallback = useCallback(() => {
    App.minimizeApp();
  }, []);
  useAndroidBackButton(backFallback);

  const shuffledBooks = useMemo(() => {
    const arr = [...homeBooks];
    // Fisher-Yates shuffle
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }, [homeBooks]);

  useEffect(() => {
    if (!useUISlice.getState().isNewSession) return;

    useAuth.signInAsGuest({
      callback: async () => {
        useUISlice.getState().setIsNewSession(false);

        // Set content language to match app language if supported
        const lang = i18n.language;
        if (CONTENT_LANGUAGES.some((l) => l.code === lang)) {
          useUserSlice.setState({ contentLanguage: lang as ContentLanguage });
        }

        await Promise.all([
          useUserSlice.getState().library().home(),
          useUserSlice
            .getState()
            .user()
            .remoteConfig({
              queryParams: { platform: getPlatform(), version },
              callback: () => {
                import("@/fetchData/firebaseAnalytics").then(
                  (firebaseAnalytics) => {
                    firebaseAnalytics.init({
                      authUser: useAuth.currentAuthUser(),
                    });
                  }
                );
              },
            }),
          useUserSlice
            .getState()
            .subscription()
            .configureRevenueCat({
              callback: async () => {
                const user = useAuth.currentAuthUser();
                import("@revenuecat/purchases-capacitor").then(
                  ({ Purchases }) => {
                    Purchases.logIn({ appUserID: user.userId });
                    Purchases.setEmail({ email: user.email });
                    Purchases.setAttributes({ ip: user.ip });
                  }
                );
              },
            }),
        ]);

        logFunnelEvent("[6] home:loaded", {
          bookCount: useUserSlice.getState().homeBooks.length,
        });
      },
    });
  }, []);

  const isLoading = homeBooks.length === 0;
  const [ageWarningBookId, setAgeWarningBookId] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const hasSeenScrollTutorial = useUserSlice(
    (state) => state.hasSeenScrollTutorial
  );
  const [showScrollTutorial, setShowScrollTutorial] = useState(false);

  useEffect(() => {
    if (!hasSeenScrollTutorial && homeBooks.length > 1) {
      setShowScrollTutorial(true);
    }
  }, [hasSeenScrollTutorial, homeBooks.length]);

  const dismissScrollTutorial = useCallback(() => {
    setShowScrollTutorial(false);
    useUserSlice.getState().preference().setHasSeenScrollTutorial();
  }, []);

  // Trigger rating prompt after scrolling past 3rd book
  const hasTriggeredRating = useRef(false);
  const handleFeedScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (hasTriggeredRating.current) return;
    const el = e.currentTarget;
    const bookIndex = Math.round(el.scrollTop / el.clientHeight);
    if (bookIndex >= 3) {
      hasTriggeredRating.current = true;
      requestReview();
    }
  }, []);

  const navigateToReader = useCallback(
    (bookId: string) => {
      const progress = readingProgress[bookId];
      if (progress) {
        router.push(
          `/native/reader?bookId=${bookId}&chapter=${progress.lastChapter}`
        );
      } else {
        router.push(`/native/reader?bookId=${bookId}&chapter=1`);
      }
    },
    [readingProgress, router]
  );

  const handleRead = useCallback((bookId: string) => {
    logFunnelEvent("[7] home:book_tapped", { bookId });
    setAgeWarningBookId(bookId);
  }, []);

  const lastReadBook = useMemo(() => {
    let best: { book: Book; lastChapter: number } | null = null;
    let bestTime = 0;
    for (const book of homeBooks) {
      const p = readingProgress[book.bookId];
      if (p) {
        const t = new Date(p.lastReadAt).getTime();
        if (t > bestTime) {
          bestTime = t;
          best = { book, lastChapter: p.lastChapter };
        }
      }
    }
    return best;
  }, [homeBooks, readingProgress]);

  return (
    <NativeContainer
      disableBottomSafeArea
      className="h-[100dvh] bg-[var(--bg-primary)] text-[var(--text-primary)] flex flex-col overflow-hidden"
    >
      {/* Skeleton loader while fetching */}
      {isLoading && (
        <div className="absolute inset-0 z-10">
          <Skeleton variant="HomeWeb" count={1} />
        </div>
      )}

      {/* Sticky continue reading bar */}
      {lastReadBook && !settingsOpen && !isLoading && (
        <div className="absolute top-[env(safe-area-inset-top,16px)] left-0 right-0 z-20 px-4 pt-4">
          <button
            onClick={() => handleRead(lastReadBook.book.bookId)}
            className="flex items-center gap-3 p-2 pr-4 rounded-2xl bg-[var(--glass-bg)] backdrop-blur-2xl shadow-lg w-full active:scale-[0.97] transition-transform text-left"
          >
            {lastReadBook.book.aiCoverUrl ? (
              <img
                src={lastReadBook.book.aiCoverUrl}
                alt=""
                className="w-10 h-14 rounded-xl object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-10 h-14 rounded-xl bg-white/10 flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-[var(--text-primary)] text-sm font-bold truncate">
                {lastReadBook.book.aiTitle}
              </p>
              <p className="text-[var(--text-secondary)] text-xs font-medium mt-0.5">
                {t("home.chapterProgress", {
                  current: lastReadBook.lastChapter,
                  total: lastReadBook.book.chapterCount,
                })}
              </p>
            </div>
            <span className="flex-shrink-0 px-4 py-2 rounded-full bg-white/20 text-[var(--text-primary)] text-xs font-bold">
              {t("home.continueButton")}
            </span>
          </button>
        </div>
      )}

      <div
        className="flex-1 overflow-y-scroll snap-y snap-mandatory no-scrollbar"
        style={{ WebkitOverflowScrolling: "touch", scrollSnapStop: "always" }}
        onScroll={handleFeedScroll}
      >
        {shuffledBooks.map((book) => (
          <FeedCard
            key={book.bookId}
            book={book}
            onRead={() => handleRead(book.bookId)}
            onReadBook={handleRead}
            onSettingsOpenChange={setSettingsOpen}
          />
        ))}
      </div>

      {showScrollTutorial && (
        <ScrollTutorial onDismiss={dismissScrollTutorial} />
      )}

      {ageWarningBookId && (
        <div
          className="fixed inset-0 z-50"
          onClick={() => setAgeWarningBookId(null)}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-md" />
          <div className="absolute inset-0 flex items-center justify-center px-8">
            <div
              className="w-full max-w-sm bg-[var(--glass-bg)] backdrop-blur-2xl rounded-[32px] p-8 shadow-2xl border border-white/10 text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-[var(--text-primary)] text-xl font-bold mb-3">
                {t("ageWarning.title")}
              </h2>
              <p className="text-[var(--text-secondary)] text-base leading-relaxed mb-8">
                {t("ageWarning.description")}
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => {
                    const bookId = ageWarningBookId;
                    setAgeWarningBookId(null);
                    if (!useUserSlice.getState().isSubscribed) {
                      useUISlice.getState().setPaywallVisible({
                        title: t("home.unlockAllChapters"),
                        utmSource: "home_book_open",
                        callback: () => navigateToReader(bookId),
                      });
                      return;
                    }
                    navigateToReader(bookId);
                  }}
                  className="w-full h-14 rounded-full bg-white/20 backdrop-blur-md text-[var(--text-primary)] font-bold text-base active:scale-[0.97] transition-transform border border-white/20"
                >
                  {t("ageWarning.over18")}
                </button>
                <button
                  onClick={() => setAgeWarningBookId(null)}
                  className="w-full h-14 rounded-full bg-white/10 text-[var(--text-secondary)] font-bold text-base active:scale-[0.97] transition-transform"
                >
                  {t("ageWarning.under18")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showNotificationPrompt && (
        <NotificationPrompt
          onAllow={handleNotificationAllow}
          onDeny={handleNotificationDeny}
        />
      )}
    </NativeContainer>
  );
};

export default Home;
